import type { components } from "@/api/contract";

export type ModelInfo = components["schemas"]["ModelInfo"];
export type ModelUsageEntry = components["schemas"]["ModelUsageEntry"];

export interface ModelGroup {
  /** The model maker (`origin`) — e.g. "meta", not the serving provider. */
  origin: string;
  models: ModelInfo[];
}

/** Human display names for model-maker keys (picker section headers). */
const ORIGIN_LABELS: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  deepseek: "DeepSeek",
  xai: "xAI",
  meta: "Meta",
  alibaba: "Alibaba",
  moonshot: "Moonshot AI",
  "z-ai": "Z.ai",
  minimax: "MiniMax",
  openrouter: "OpenRouter",
};

/** Display label for a model-maker key — mapped name, else the raw key. */
export function originLabel(origin: string): string {
  return ORIGIN_LABELS[origin] ?? origin;
}

/** Display label for a model — `display_name` wins, then `name`, then `id`. */
export function modelLabel(model: ModelInfo): string {
  return model.display_name || model.name || model.id;
}

/** Whether a model is usable on the free plan (drives the plan badge in the picker). */
export function isFreeModel(model: ModelInfo): boolean {
  return model.plans.length === 0 || model.plans.includes("free");
}

/** A model-access plan id advertised by the backend catalog. */
export type PlanTier = ModelInfo["plans"][number];

/** Whether the given plan may use this model — free models are open to everyone. */
export function modelAllowedForPlan(model: ModelInfo, plan: PlanTier): boolean {
  if (isFreeModel(model)) return true;
  return model.plans.includes(plan);
}

/**
 * The cheapest paid plan that unlocks a gated model — the plan a paywall should
 * sell. Returns null when the model needs no upgrade (free, or unrestricted).
 */
export function requiredPlanFor(model: ModelInfo): "creator" | "power" | "studio" | null {
  if (isFreeModel(model)) return null;
  if (model.plans.includes("creator") || model.plans.includes("pro")) return "creator";
  if (model.plans.includes("power")) return "power";
  if (model.plans.includes("studio") || model.plans.includes("max")) return "studio";
  return null;
}

/**
 * Models the user touched most recently, newest first, capped. `usage` already
 * arrives most-recent-first; we map ids to live models and drop any that are no
 * longer offered.
 */
export function recentModels(
  models: ModelInfo[],
  usage: ModelUsageEntry[],
  limit = 4,
): ModelInfo[] {
  const byId = new Map(models.map((m) => [m.id, m]));
  const out: ModelInfo[] = [];
  const seen = new Set<string>();
  for (const entry of usage) {
    const model = byId.get(entry.model_id);
    if (model && !seen.has(model.id)) {
      out.push(model);
      seen.add(model.id);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/**
 * Group models by their maker (`origin`) — makers in first-appearance order,
 * models within a group by `sort_order` (lower = newer) then label. Origin, not
 * the serving provider: OpenRouter-served models (Llama, Qwen, Kimi, …) group
 * under Meta/Alibaba/Moonshot rather than one "openrouter" bucket.
 */
export function groupModelsByOrigin(models: ModelInfo[]): ModelGroup[] {
  const groups = new Map<string, ModelInfo[]>();
  for (const model of models) {
    const key = model.origin || model.provider;
    const list = groups.get(key);
    if (list) list.push(model);
    else groups.set(key, [model]);
  }
  return [...groups.entries()].map(([origin, list]) => ({
    origin,
    models: [...list].sort(
      (a, b) => a.sort_order - b.sort_order || modelLabel(a).localeCompare(modelLabel(b)),
    ),
  }));
}

/** Case-insensitive filter over label + maker + provider. Blank query returns all. */
export function filterModels(models: ModelInfo[], query: string): ModelInfo[] {
  const q = query.trim().toLowerCase();
  if (!q) return models;
  return models.filter(
    (m) =>
      modelLabel(m).toLowerCase().includes(q) ||
      m.provider.toLowerCase().includes(q) ||
      (m.origin || "").toLowerCase().includes(q) ||
      originLabel(m.origin || "").toLowerCase().includes(q),
  );
}

/**
 * Choose the active model for a composer: the conversation's own model if it's
 * still offered, else the most recently used, else the first available. Returns
 * null only when there are no models at all.
 */
export function pickDefaultModelId(
  models: ModelInfo[],
  usage: ModelUsageEntry[],
  preferredId?: string | null,
): string | null {
  if (preferredId && models.some((m) => m.id === preferredId)) return preferredId;
  const recent = recentModels(models, usage, 1);
  if (recent.length) return recent[0].id;
  return models[0]?.id ?? null;
}
